import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"

const chartTypes = [
  { value: "daily", label: "dashboard.freq.daily" },
  { value: "weekly", label: "dashboard.freq.weekly" },
  { value: "monthly", label: "dashboard.freq.monthly" },
  { value: "yearly", label: "dashboard.freq.yearly" },
] as const

const chartPlaceholderSx = {
  height: 230,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "grey.50",
  backgroundImage:
    "repeating-linear-gradient(to right, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 28px), repeating-linear-gradient(to bottom, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 24px)",
}

const legendItemSx = {
  width: 12,
  height: 12,
  borderRadius: 0.5,
}

export const DashboardDeliveredReturnedOrdersChart: FC = () => {
  const { t } = useTranslation()
  const [frequency, setFrequency] = useState("daily")
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  )
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"))

  return (
    <Card
      {...uiSurface("UI-DLV2-073")}
      data-testid="dashboard-delivered-returned-card"
      variant="outlined"
      sx={{ flex: 1, borderRadius: 2 }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t("dashboard.deliveredReturned")}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 1.5 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <InputLabel>{t("dashboard.chart")}</InputLabel>
            <Select
              data-testid="dashboard-delivered-returned-frequency-select"
              value={frequency}
              label={t("dashboard.chart")}
              onChange={(e) => setFrequency(e.target.value)}
            >
              {chartTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {t(type.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            data-testid="dashboard-delivered-returned-from-input"
            size="small"
            type="date"
            label={t("dashboard.from")}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            data-testid="dashboard-delivered-returned-to-input"
            size="small"
            type="date"
            label={t("dashboard.to")}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            data-testid="dashboard-delivered-returned-display-btn"
            variant="contained"
            size="small"
          >
            {t("dashboard.display")}
          </Button>
        </Stack>

        <Box sx={chartPlaceholderSx}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            sx={{ height: "100%", px: 2, py: 2 }}
          >
            {[30, 42, 56, 38, 68, 50, 63, 74, 60, 47].map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  height: `${height}%`,
                  bgcolor: index % 2 === 0 ? "primary.light" : "grey.300",
                  opacity: 0.75,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          sx={{ mt: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ ...legendItemSx, bgcolor: "primary.light" }} />
            <Typography variant="caption" color="text.secondary">
              {t("dashboard.legend.ordersCompleted")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ ...legendItemSx, bgcolor: "grey.400" }} />
            <Typography variant="caption" color="text.secondary">
              {t("dashboard.legend.returnedOrders")}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Box
            sx={{
              flex: 1,
              bgcolor: "primary.50",
              py: 2,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              0
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.ordersCompleted")}
            </Typography>
          </Box>
          <Stack
            flex={1}
            spacing={0.75}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.returnedOrders")}: 0 (0.0 %)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("dashboard.ordersTakenByCourier")}: 0
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ alignSelf: "flex-start" }}
            >
              {t("dashboard.kpi.success")}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
